/**
 * The public creator profile — `GET /v1/u/{handle}` (backend#240).
 *
 * Not the same thing as the API's unversioned `GET /u/{handle}`, which
 * resolves a handle to its pinned bio clip and returns the clip (backend#312).
 * This is the other read: who the creator is and everything they have
 * published, rather than the one thing they pinned.
 *
 * Since #237 both reads back **one** public URL, `/clip/{handle}`: the bio
 * clip when there is one, this profile when there is not. See
 * `docs/decisions/237-creator-page-url.md`.
 *
 * Three fields are null or empty in every response today, and only one of the
 * three is something the page may derive for itself:
 *
 *   - `avatar_url` is hard-coded `null` in `services/public_profile.py`. The
 *     usual explanation — "no avatar storage exists" — is **wrong**: media is
 *     harvested (every `hero` component in production carries an `image_url`),
 *     publish renders a per-clip OG PNG, and `/image-proxy` is live. What was
 *     missing was a *choice*. The page now makes one, in
 *     `api/_lib/creatorImage.ts` (#236): `avatar_url` if the server ever
 *     returns it, else the card we render for the clip the creator pinned as
 *     their page, else initials. **Never an image from a link inside a clip**
 *     — one of the values in that column right now is a stranger's profile
 *     picture, and the avatar slot is a claim about who the page belongs to.
 *     Whatever ends up there has also been through `/image-proxy` (#238), so
 *     no consumer can hotlink one by accident.
 *   - `topics` is always `[]` — no topic taxonomy exists on threads.
 *   - There is no follower count in the payload at all, because there is no
 *     follow graph. #240 dropped the stat rather than inventing a number, and
 *     iOS did the same.
 *
 * `counts` is over **published** material only. The public page must not
 * disclose how much private material a creator has, so this is not "threads"
 * in the sense `/me` means it.
 *
 * 404 covers malformed, unknown, released and private-profile handles
 * indistinguishably, so probing cannot reveal which handles exist. That means
 * a 404 here is "no public profile", never "this person does not exist", and
 * the page has to say the former.
 */

import { proxiedImageURL } from "@/lib/image-proxy";

const API_URL = import.meta.env.VITE_API_URL || "https://api.manticscore.com";

/**
 * Matches the server's normalisation: strip a leading `@`, lowercase.
 *
 * Since #237 this is also the **routing** discriminator for `/clip/{x}`, and
 * therefore the single source of truth for a pattern that has to be spelled a
 * second time in `vercel.json` (JSON, so it cannot import this). Those two
 * spellings are pinned together by `src/lib/creator-routing.test.ts` — change
 * this and that test tells you which rewrite to change with it.
 *
 * **The leading character must be alphanumeric, and that is not cosmetic
 * (web#251).** This used to be `^[a-z0-9_]{3,30}$`, which matched the
 * bio-page grammar (`services/bio_page.py`) but NOT the handle-namespace
 * grammar (`services/public_profile.py`, `^[a-z0-9][a-z0-9_]{2,29}$`). The
 * server writes both stores on a claim and, when the two disagree, silently
 * skips the namespace write — so `_bair` returned 200 having only half
 * applied: the bio page resolved and `GET /v1/u/_bair` 404'd forever. Since
 * #237 made `/clip/{handle}` the canonical creator URL, the address we tell
 * the creator to share was the broken half.
 *
 * Accepting a name the user can never actually own is a worse error than
 * rejecting it, so this now matches the strictest grammar in the system. The
 * backend half — making that skip loud instead of silent — is
 * manticscore-backend#496.
 */
export const HANDLE_RE = /^[a-z0-9][a-z0-9_]{2,29}$/;

/**
 * Is this `/clip/` path segment a handle rather than a clip slug?
 *
 * Shape alone answers it, with no lookup, because the two namespaces are
 * disjoint: every minted clip slug contains a hyphen (`s-`/`v-`/`r-`/`a-`/
 * `live-` + hex, `f"s-{secrets.token_hex(8)}"`) and a handle can never contain
 * one — underscore is the only punctuation `HANDLE_RE` admits.
 *
 * **Case-insensitive, and that is not a nicety.** It has to agree with the
 * `vercel.json` rewrite that makes the same decision one layer earlier, and
 * Vercel compiles a `source` through path-to-regexp with `sensitive: false` —
 * `/clip/:handle([a-z0-9][a-z0-9_]{2,29})` becomes
 * `/^\/clip(?:\/([a-z0-9][a-z0-9_]{2,29}))[\/#\?]?$/i`, so `/clip/Bair` matches at the
 * edge whatever the character class says. A case-sensitive test here would
 * have sent a crawler the creator page (u-og lowercases the handle) and the
 * human "Brief not found", at the same URL. `src/lib/creator-routing.test.ts`
 * models the edge regex with the `i` flag for exactly this reason.
 *
 * It does not strip a leading `@`: a URL segment is a path, and `/clip/@bair`
 * is not a path the product mints. `normalizeHandle` is for user input.
 */
export function isHandleSegment(segment: string): boolean {
  return HANDLE_RE.test(segment.toLowerCase());
}

export interface CreatorClip {
  title: string | null;
  slug: string;
  link_count: number;
  published_at: string | null;
}

export interface CreatorProfile {
  handle: string;
  display_name: string;
  bio: string | null;
  /**
   * Null in every response today, and authoritative the day it is not: the
   * derivation in `creatorIdentityImage` is a fallback beneath this, not a
   * replacement for it (#236).
   *
   * When it is non-null it has already been through `/image-proxy` —
   * `readCreatorProfile` rewrites it (#238) — so never re-derive it from a raw
   * payload.
   */
  avatar_url: string | null;
  counts: { links: number; threads: number };
  /** At most 6, in the creator's chosen order. */
  featured_threads: CreatorClip[];
  /** Always empty today. */
  topics: string[];
  /** At most 12, newest first. */
  published_clips: CreatorClip[];
}

export function normalizeHandle(raw: string): string {
  return raw.trim().replace(/^@/, "").toLowerCase();
}

export type CreatorResult =
  | { status: "ok"; profile: CreatorProfile }
  /** 404 — no public profile. Deliberately not "no such person". */
  | { status: "not_found" }
  | { status: "failed" };

export async function readCreatorProfile(
  rawHandle: string,
  signal?: AbortSignal,
): Promise<CreatorResult> {
  const handle = normalizeHandle(rawHandle);
  // The server 404s a malformed handle anyway; not spending the request keeps
  // a junk URL from looking like an outage.
  if (!HANDLE_RE.test(handle)) return { status: "not_found" };

  try {
    const response = await fetch(`${API_URL}/v1/u/${encodeURIComponent(handle)}`, {
      headers: { Accept: "application/json" },
      signal,
    });
    if (response.status === 404) return { status: "not_found" };
    if (!response.ok) return { status: "failed" };
    const profile = (await response.json()) as CreatorProfile;
    if (!profile || typeof profile.handle !== "string") return { status: "failed" };
    // Read-time rewrite, so the rule holds at the boundary rather than at each
    // place the field is rendered (#238). `avatar_url` is the only image field
    // this payload has, and everything that could ever fill it is harvested
    // from a third party — a raw one in an `<img src>` on a public profile
    // announces every visitor to whatever host holds it. Same-origin values
    // pass through untouched; a value with no proxiable form becomes null,
    // which every consumer already handles because the field is null today.
    return {
      status: "ok",
      profile: { ...profile, avatar_url: proxiedImageURL(profile.avatar_url) },
    };
  } catch {
    return { status: "failed" };
  }
}

/**
 * The one public URL a creator has. #237.
 *
 * There used to be two — `/u/{handle}` for the pinned bio clip and
 * `/clip/u/{handle}` for the auto-index of everything published — and in
 * production they served byte-identical HTML, because the split depends on
 * `bio_session_id` and that is NULL on every account. Two public URLs for one
 * creator, differing only by prefix, is a coin flip for anyone reading one.
 * Both are now 308s to this.
 *
 * Under `/clip/` on purpose, and this is the reason it is not `/{handle}` or
 * `/u/{handle}`. Production AASA claims `/clip`, `/clip/*` and `/clip/live/*`
 * for the App Clip appID and then **excludes `/*`** — Apple evaluates
 * components first-rule-wins, so a creator link at any other path is
 * explicitly excluded from App Clip invocation and opens Safari. `/clip/` is
 * the only namespace that works with no AASA change, and AASA is the component
 * that fails silently and takes Apple's cache with it.
 *
 * It is also the accurate noun rather than a squat: under
 * `docs/decisions/162-sublink-studio-scope.md` a composed creator page *is* a
 * clip (`origin = 'user_added'`).
 */
export function creatorPagePath(handle: string): string {
  return `/clip/${encodeURIComponent(handle)}`;
}

/**
 * The former names — `bioClipPath` (`/u/{handle}`) and `creatorProfilePath`
 * (`/clip/u/{handle}`) — are gone rather than aliased. Keeping two names for
 * one URL is how the two-URL confusion #237 closed would grow back.
 */
